import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/RegisterModal.css'; // Import the CSS for RegisterModal

function RegisterModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    teamName: '',
    captainName: '',
    email: '',
    phone: '',
    eventId: '',
    players: ['', '', '', '', ''],
    terms: false
  });

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookingId, setBookingId] = useState(null);

  // Load events from localStorage or use default
  useEffect(() => {
    // You can fetch events from your data source
    const defaultEvents = [
      { id: 2, title: "The Crown Championship 2026 ⚽", sport: "Football", date: "July 27, 2026", fee: "₹4800 per team" }
    ];
    setEvents(defaultEvents);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handlePlayerChange = (index, value) => {
    const newPlayers = [...formData.players];
    newPlayers[index] = value;
    setFormData({ ...formData, players: newPlayers });
  };

  const addPlayer = () => {
    setFormData({
      ...formData,
      players: [...formData.players, '']
    });
  };

  const removePlayer = (index) => {
    if (formData.players.length <= 5) return;
    const newPlayers = formData.players.filter((_, i) => i !== index);
    setFormData({ ...formData, players: newPlayers });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate API call
    setTimeout(() => {
      const booking = {
        id: Date.now(),
        ...formData,
        registeredAt: new Date().toISOString()
      };

      // Save to localStorage
      const existingBookings = JSON.parse(localStorage.getItem('bookings') || '[]');
      localStorage.setItem('bookings', JSON.stringify([...existingBookings, booking]));

      setBookingId(booking.id);
      setSubmitted(true);
      setLoading(false);

      // Reset form after 5 seconds
      setTimeout(() => {
        setSubmitted(false);
        setBookingId(null);
        setFormData({
          teamName: '',
          captainName: '',
          email: '',
          phone: '',
          eventId: '',
          players: ['', '', '', '', ''],
          terms: false
        });
        onClose();
      }, 3000);
    }, 1500);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="modal-close-btn" onClick={onClose}>
          ✕
        </button>

        {/* Modal Content */}
        <div className="modal-content">
          {submitted ? (
            // Success State
            <div className="modal-success">
              <span className="modal-success-icon">✅</span>
              <h2>Registration Successful!</h2>
              <p>Your team has been registered successfully.</p>
              <div className="modal-success-booking">
                <span>Booking ID</span>
                <strong>#{bookingId}</strong>
              </div>
              <p className="modal-success-note">
                A confirmation email has been sent to your registered email address.
              </p>
              <button className="modal-success-btn" onClick={onClose}>
                Close
              </button>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="modal-header">
                <span className="modal-icon">🎯</span>
                <h2>Register Your Team</h2>
                <p>Fill in the details to register for an upcoming event</p>
              </div>

              {/* Form */}
              <form className="modal-form" onSubmit={handleSubmit}>
                {/* Event Selection */}
                <div className="modal-form-group">
                  <label>
                    Select Event <span className="modal-required">*</span>
                  </label>
                  <select
                    name="eventId"
                    value={formData.eventId}
                    onChange={handleChange}
                    required
                    className="modal-select"
                  >
                    <option value="">Choose an event...</option>
                    {events.map(event => (
                      <option key={event.id} value={event.id}>
                        {event.sport} - {event.title} ({event.date})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Team Name */}
                <div className="modal-form-group">
                  <label>
                    Team Name <span className="modal-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="teamName"
                    value={formData.teamName}
                    onChange={handleChange}
                    required
                    placeholder="Enter your team name"
                    className="modal-input"
                  />
                </div>

                {/* Captain Name */}
                <div className="modal-form-group">
                  <label>
                    Captain Name <span className="modal-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="captainName"
                    value={formData.captainName}
                    onChange={handleChange}
                    required
                    placeholder="Enter captain name"
                    className="modal-input"
                  />
                </div>

                {/* Email & Phone */}
                <div className="modal-form-row">
                  <div className="modal-form-group">
                    <label>
                      Email <span className="modal-required">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="captain@email.com"
                      className="modal-input"
                    />
                  </div>
                  <div className="modal-form-group">
                    <label>
                      Phone <span className="modal-required">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="9876543210"
                      className="modal-input"
                    />
                  </div>
                </div>

                {/* Players */}
                <div className="modal-form-group">
                  <div className="modal-players-label">
                    <label>
                      Team Players <span className="modal-required">*</span>
                    </label>
                    <span className="modal-players-count">
                      {formData.players.filter(p => p.trim()).length} players added
                    </span>
                  </div>
                  {formData.players.map((player, index) => (
                    <div key={index} className="modal-player-input">
                      <input
                        type="text"
                        placeholder={`Player ${index + 1}`}
                        value={player}
                        onChange={(e) => handlePlayerChange(index, e.target.value)}
                        className="modal-input"
                      />
                      {index >= 5 && (
                        <button type="button" className="modal-remove-btn" onClick={() => removePlayer(index)}>
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" className="modal-add-btn" onClick={addPlayer}>
                    + Add Player
                  </button>
                </div>

                {/* Terms */}
                <label className="modal-checkbox">
                  <input
                    type="checkbox"
                    name="terms"
                    checked={formData.terms}
                    onChange={handleChange}
                    required
                  />
                  <span>
                    I agree to the <strong>terms and conditions</strong> <span className="modal-required">*</span>
                  </span>
                </label>

                {/* Submit */}
                <button type="submit" className="modal-submit-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="modal-loader"></span>
                      Registering...
                    </>
                  ) : (
                    '✅ Register Team'
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default RegisterModal;